import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import { AuthContext } from "context/Authentication";
import {
  postRecord,
  handleApiResponse,
  handleApiError,
} from "services/services";
import loadImage from "templates/default/assets/media/load_image.png";
import SnackBar from "CommonComponents/Snackbar/SnackBar.jsx";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useNavigate } from "react-router-dom";
import Skeleton from "@mui/material/Skeleton";

const API_GetWishlistByUserId = "wishlist/getbyuserid";
const API_DeleteWishlist = "wishlist/delete";
const API_AddToCart = "cart/create";
const API_UpdateCart = "cart/update";
const API_GetCartByUserId = "cart/getbyuserid";
const API_GetCombinationById = "productcombinations/getbyid";
const API_GetProductMedia = "productmedia/getbyproductid";
const API_GetProductById = "products/getbyid";
const API_GetInventoryByCombinationId = "inventory/getbycombinationid";

export default function Wishlist({ setHeaderRefresh }) {
  const context = useContext(AuthContext);
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState([]);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackOptions, setSnackOptions] = useState({
    color: "success",
    message: "Hi",
  });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (context.state.isAuthenticated) {
      fetchWishlist(isMounted);
    }

    return () => {
      isMounted = false;
    };
  }, [context.state.isAuthenticated]);

  const fetchWishlist = async (isMounted) => {
    try {
      setProcessing(true);
      const response = await postRecord(API_GetWishlistByUserId, {
        User_Id: context.state.user.User_Id,
      });

      if (!isMounted) return;

      if (response.status === 200 && response.data) {
        const wishlistItems = response.data;
        const productPromises = wishlistItems.map((item) =>
          postRecord(API_GetCombinationById, {
            Combination_Id: item.Combination_Id,
          }).then((combinationResponse) => {
            if (
              combinationResponse.status === 200 &&
              combinationResponse.data
            ) {
              return postRecord(API_GetProductById, {
                Product_Id: item.Product_Id,
              }).then((productResponse) => {
                if (productResponse.status === 200 && productResponse.data) {
                  return postRecord(API_GetProductMedia, {
                    Product_ID: item.Product_Id,
                  }).then((mediaResponse) => {
                    if (mediaResponse.status === 200 && mediaResponse.data) {
                      const defaultMedia = mediaResponse.data.find(
                        (media) => media.Combination_Id === item.Combination_Id
                      );
                      return postRecord(API_GetInventoryByCombinationId, {
                        Combination_Id: item.Combination_Id,
                      }).then((inventoryResponse) => {
                        if (
                          inventoryResponse.status === 200 &&
                          inventoryResponse.data
                        ) {
                          return {
                            ...item,
                            image: defaultMedia
                              ? defaultMedia.Media_File_Path
                              : loadImage,
                            Product_Full_Name:
                              productResponse.data.Product_Full_Name,
                            Short_Description:
                              productResponse.data.Short_Description,
                            Combination_Text:
                              combinationResponse.data.Combination_Text,
                            price:
                              inventoryResponse.data[0].Total_Selling_Price,
                          };
                        }
                      });
                    }
                  });
                }
              });
            }
          })
        );
        const wishlistWithDetails = await Promise.all(productPromises);

        if (!isMounted) return;
        setWishlist(wishlistWithDetails);

        // Update the header after refreshing wishlist data
        if (setHeaderRefresh) {
          setHeaderRefresh(true);
        }
      }
    } catch (error) {
      if (isMounted) {
        handleApiError(error, setSnackOptions, setProcessing, setSnackOpen);
      }
    } finally {
      if (isMounted) setProcessing(false);
    }
  };

  const handleRemoveItem = async (wishlistId) => {
    let isMounted = true;

    try {
      setProcessing(true);
      const response = await postRecord(API_DeleteWishlist, {
        Wishlist_Id: wishlistId,
        Deleted_By: context.state.user.User_Id,
      });

      if (!isMounted) return;

      handleApiResponse(
        response,
        setSnackOptions,
        setProcessing,
        setSnackOpen,
        () => {
          // First update the local state
          setWishlist(
            wishlist.filter((item) => item.Wishlist_Id !== wishlistId)
          );

          // Then refresh wishlist data from the server
          fetchWishlist(isMounted);

          // Ensure header is refreshed
          if (setHeaderRefresh) {
            setHeaderRefresh(true);
          }
        }
      );
    } catch (error) {
      if (isMounted) {
        handleApiError(error, setSnackOptions, setProcessing, setSnackOpen);
      }
    } finally {
      if (isMounted) setProcessing(false);
    }
  };

  const addToCart = async (product) => {
    let isMounted = true;

    if (!context.state.isAuthenticated) {
      alert("Please log in to add items to the cart.");
      return;
    }

    try {
      const cartResponse = await postRecord(API_GetCartByUserId, {
        User_Id: context.state.user.User_Id,
      });

      if (!isMounted) return;

      let existingCartItem = null;
      if (cartResponse.status === 200 && cartResponse.data) {
        existingCartItem = cartResponse.data.find(
          (item) => item.Combination_Id === product.Combination_Id
        );
      }

      if (existingCartItem) {
        const updatedCartItem = {
          ...existingCartItem,
          Units: existingCartItem.Units + 1,
          Total_Price:
            (existingCartItem.Units + 1) * existingCartItem.Price_Per_Unit,
          Modified_By: context.state.user.User_Id,
        };

        try {
          const response = await postRecord(API_UpdateCart, updatedCartItem);
          if (isMounted) {
            handleApiResponse(
              response,
              setSnackOptions,
              setProcessing,
              setSnackOpen,
              async () => {
                // Remove from wishlist after successful cart update
                await handleRemoveItem(product.Wishlist_Id);

                // Refresh wishlist data from the server
                fetchWishlist(isMounted);

                // Ensure header is refreshed
                if (setHeaderRefresh) {
                  setHeaderRefresh(true);
                }
              }
            );
          }
        } catch (error) {
          if (isMounted) {
            console.log("error-----", error);
            handleApiError(error, setSnackOptions, setProcessing, setSnackOpen);
          }
        }
      } else {
        const newCartItem = {
          User_Id: context.state.user.User_Id,
          Product_Id: product.Product_Id,
          Combination_Id: product.Combination_Id,
          Units: 1,
          Price_Per_Unit: product.price,
          Total_Price: product.price,
          Is_Savefor_Later: "0",
          Added_By: context.state.user.User_Id,
        };

        try {
          const response = await postRecord(API_AddToCart, newCartItem);
          if (isMounted) {
            handleApiResponse(
              response,
              setSnackOptions,
              setProcessing,
              setSnackOpen,
              async () => {
                // Remove from wishlist after successful cart addition
                await handleRemoveItem(product.Wishlist_Id);

                // Refresh wishlist data from the server
                fetchWishlist(isMounted);

                // Ensure header is refreshed
                if (setHeaderRefresh) {
                  setHeaderRefresh(true);
                }
              }
            );
          }
        } catch (error) {
          if (isMounted) {
            handleApiError(error, setSnackOptions, setProcessing, setSnackOpen);
          }
        }
      }
    } catch (error) {
      if (isMounted) {
        handleApiError(error, setSnackOptions, setProcessing, setSnackOpen);
      }
    }
  };

  return (
    <Box>
      {processing && wishlist.length === 0 ? (
        <Grid container spacing={2}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
              <Card
                sx={{
                  mx: 1,
                  boxShadow: "none",
                  background: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "12px",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                }}
              >
                <Skeleton variant="rectangular" height={200} />
                <CardContent>
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : wishlist.length === 0 ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <ShoppingCartIcon sx={{ fontSize: 80, color: "secondary" }} />
          <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
            Your wishlist is empty
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            size="large"
            onClick={() => navigate("/")}
          >
            Continue Shopping
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {wishlist.map((product) => (
            <Grid item xs={12} sm={6} md={4} lg={4} key={product.Wishlist_Id}>
              <Card
                sx={{
                  mx: 1,
                  boxShadow: "none",
                  transition: "transform 0.3s ease",
                  background: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "12px",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  cursor: "pointer",
                  textAlign: "center",
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
              >
                <CardMedia
                  sx={{ height: 200 }}
                  image={
                    product.image
                      ? process.env.BASEURL + product.image
                      : loadImage
                  }
                  title={product.Product_Full_Name}
                />
                <CardContent sx={{ textAlign: "left" }}>
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    {product.Product_Full_Name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {product.Short_Description}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {product.Combination_Text}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontSize: "1rem" }}>
                      <small
                        style={{
                          fontWeight: "500",
                          fontSize: "8px",
                          marginRight: "2px",
                        }}
                      >
                        {context.state?.companyInfo?.Currency}
                      </small>
                      {product.price?.toFixed(2)}
                    </Typography>
                    <Box>
                      <IconButton onClick={() => addToCart(product)}>
                        <AddShoppingCartIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleRemoveItem(product.Wishlist_Id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      <SnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        options={snackOptions}
      />
    </Box>
  );
}
